import { Alert, App, Button, Drawer, List, Space, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useOutbox } from '@/offline/context';
import type { OutboxOp } from '@/offline/outbox';

const DATETIME = 'DD.MM.YYYY HH:mm';

/**
 * Статус связи и очереди всегда на виду (ADR-0009 п. 6): «Нет связи · в очереди 3».
 * Отклонённые сервером операции не пропадают молча — «Требует внимания» (п. 5, T-183).
 */
export function OfflineBanner() {
  const { t } = useTranslation();
  const { online, queued, failed, sending, flush } = useOutbox();
  const [open, setOpen] = useState(false);

  return (
    <Space
      direction="vertical"
      style={{ width: '100%', marginBottom: queued.length || failed.length || !online ? 16 : 0 }}
    >
      {!online && (
        <Alert
          type="warning"
          showIcon
          message={t('offline.noConnection', { count: queued.length })}
          description={t('offline.noConnectionHint')}
        />
      )}
      {online && queued.length > 0 && (
        <Alert
          type="info"
          showIcon
          message={t('offline.queued', { count: queued.length })}
          action={
            <Button size="small" loading={sending} onClick={() => void flush()}>
              {t('offline.sendNow')}
            </Button>
          }
        />
      )}
      {failed.length > 0 && (
        <Alert
          type="error"
          showIcon
          message={t('offline.attention', { count: failed.length })}
          action={
            <Button
              size="small"
              danger
              onClick={() => {
                setOpen(true);
              }}
            >
              {t('offline.open')}
            </Button>
          }
        />
      )}
      <AttentionDrawer
        open={open}
        onClose={() => {
          setOpen(false);
        }}
      />
    </Space>
  );
}

/** «Требует внимания»: причина отказа и что можно сделать — повторить, исправить, удалить. */
function AttentionDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t } = useTranslation();
  const { modal } = App.useApp();
  const { failed, retry, discard } = useOutbox();

  function confirmDiscard(op: OutboxOp) {
    modal.confirm({
      title: t('offline.discardTitle'),
      content: op.label,
      okText: t('offline.discardOk'),
      okButtonProps: { danger: true },
      cancelText: t('common.cancel'),
      autoFocusButton: 'cancel',
      onOk: () => {
        discard(op.key);
      },
    });
  }

  /** Похожий товар уже есть: «создать новый» — та же приёмка с подтверждением нового товара. */
  function createNewProduct(op: OutboxOp) {
    const product = op.body.new_product;
    if (typeof product !== 'object' || product === null) return;
    retry(op.key, { ...op.body, new_product: { ...product, confirm_new: true } });
  }

  return (
    <Drawer open={open} onClose={onClose} title={t('offline.attentionTitle')} width={560}>
      <Typography.Paragraph type="secondary">{t('offline.attentionHint')}</Typography.Paragraph>
      <List
        dataSource={failed}
        locale={{ emptyText: t('offline.attentionEmpty') }}
        renderItem={(op) => (
          <List.Item
            actions={[
              ...(op.errorCode === 'similar_products'
                ? [
                    <Button
                      key="new"
                      size="small"
                      onClick={() => {
                        createNewProduct(op);
                      }}
                    >
                      {t('offline.createNew')}
                    </Button>,
                  ]
                : []),
              <Button
                key="retry"
                size="small"
                onClick={() => {
                  retry(op.key);
                }}
              >
                {t('offline.retry')}
              </Button>,
              <Button
                key="discard"
                size="small"
                danger
                onClick={() => {
                  confirmDiscard(op);
                }}
              >
                {t('offline.discard')}
              </Button>,
            ]}
          >
            <Space direction="vertical" size={2}>
              <Space wrap>
                <Tag>{t(`offline.kind.${op.kind}`)}</Tag>
                <strong>{op.label}</strong>
              </Space>
              <Typography.Text type="secondary">
                {t('offline.madeAt', { at: dayjs(op.createdAt).format(DATETIME) })}
              </Typography.Text>
              <Typography.Text type="danger">{op.error}</Typography.Text>
            </Space>
          </List.Item>
        )}
      />
    </Drawer>
  );
}
